import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/homepage', icon: 'Home' },
    { label: 'About', path: '/about-us', icon: 'Info' },
    { label: 'Products', path: '/product-portfolio', icon: 'Package' },
    { label: 'Contact', path: '/contact', icon: 'Phone' }
  ];

  const isActivePath = (path) => {
    if (path === '/homepage' && location.pathname === '/') return true;
    return location.pathname === path || location.pathname?.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <motion.header 
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-modern border-b border-border/50 shadow-modern"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <a href="/homepage" className="flex items-center space-x-3 group">
              <div className="flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-glow group-hover:shadow-glow-hover transition-all duration-300 p-2">
                <Image
                  src="/assets/images/Endo-Logo-small.png"
                  alt="Endosurgery Malaysia Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-text-primary leading-tight">
                  Endosurgery
                </span>
                <span className="text-xs font-medium text-text-secondary leading-tight">
                  Malaysia
                </span>
              </div>
            </a>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            {navigationItems?.map((item, index) => (
              <motion.a
                key={item?.path}
                href={item?.path}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 animate-lift-hover ${
                  isActivePath(item?.path)
                    ? 'text-primary bg-primary/10 border border-primary/20 shadow-glow' 
                    : 'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                }`}
                aria-current={isActivePath(item?.path) ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </motion.a>
            ))}
          </nav>

          {/* Desktop CTA & Theme Toggle */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center space-x-2 px-3 py-1 bg-success/10 rounded-full border border-success/20">
              <Icon name="Shield" size={14} color="var(--color-success)" />
              <span className="text-xs font-medium text-success">GDPMD Certified</span>
            </div>
            <ThemeToggle />
            <Button 
              variant="default" 
              size="sm" 
              iconName="ArrowRight" 
              iconPosition="right" 
              className="animate-lift-hover"
              onClick={() => window.location.href = '/contact'}
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-colors duration-200 "
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden border-t border-border/50 backdrop-blur-modern"
            >
              <nav className="px-4 py-4 space-y-2" role="navigation" aria-label="Mobile navigation">
                {navigationItems?.map((item, index) => (
                  <motion.a
                    key={item?.path}
                    href={item?.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                      isActivePath(item?.path)
                        ? 'text-primary bg-primary/10 border border-primary/20' 
                        : 'text-text-secondary hover:text-text-primary hover:bg-muted/50'
                    }`}
                    aria-current={isActivePath(item?.path) ? 'page' : undefined}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                  </motion.a>
                ))}
                
                {/* Mobile CTA */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                  className="pt-4 border-t border-border/50 mt-4 space-y-3"
                >
                  <div className="flex items-center justify-center space-x-2 px-3 py-2 bg-success/10 rounded-lg border border-success/20">
                    <Icon name="Shield" size={16} color="var(--color-success)" />
                    <span className="text-sm font-medium text-success">GDPMD Certified</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <ThemeToggle />
                  </div>
                  <Button 
                    variant="default" 
                    fullWidth 
                    iconName="ArrowRight" 
                    iconPosition="right" 
                    className="animate-lift-hover"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Get Quote
                  </Button>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;